import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Inbox, Loader2, Send } from "lucide-react";
import { api, errMsg, fmtDate, ROLE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Card = ({ icon: I, title, children, testId }) => (
  <div className="bg-white border rounded-xl p-5 space-y-4 lg:col-span-2" data-testid={testId}>
    <h3 className="font-display font-bold flex items-center gap-2"><I className="h-4 w-4 text-[#1E3A8A]" />{title}</h3>
    {children}
  </div>
);

export function AdminRoleModels() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/ai-models").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!cfg) return null;
  const setRole = (role, patch) => {
    const cur = { ...cfg.roles[role], ...patch };
    if (!cur.allowed.includes(cur.default)) cur.default = cur.allowed[0];
    setCfg({ ...cfg, roles: { ...cfg.roles, [role]: cur } });
  };
  const toggle = (role, id) => {
    const allowed = cfg.roles[role].allowed.includes(id) ? cfg.roles[role].allowed.filter((m) => m !== id) : [...cfg.roles[role].allowed, id];
    if (allowed.length) setRole(role, { allowed: cfg.models.map((m) => m.id).filter((m) => allowed.includes(m)) });
  };
  const save = async () => {
    try { const { data } = await api.put("/admin/ai-models", { roles: cfg.roles }); setCfg({ ...cfg, roles: data }); toast.success("Modèles IA enregistrés"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card icon={Bot} title="Modèles IA par profil" testId="role-models-card">
      <p className="text-sm text-slate-600">Choisissez les modèles que chaque profil peut utiliser dans l'assistant et celui proposé par défaut. Les modèles « raisonnement » (Opus, GPT 6 Sol) coûtent plus cher par question.</p>
      <div className="border rounded-lg divide-y">
        {Object.entries(cfg.roles).map(([role, rc]) => (
          <div key={role} className="p-3 grid md:grid-cols-[130px_1fr_240px] gap-3 md:items-center" data-testid={`role-models-${role}`}>
            <div className="text-sm font-semibold">{ROLE_LABELS[role] || role}</div>
            <div className="flex flex-wrap gap-1.5">
              {cfg.models.map((m) => (
                <button key={m.id} type="button" onClick={() => toggle(role, m.id)} data-testid={`role-model-${role}-${m.id}`}
                  className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${rc.allowed.includes(m.id) ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-500 hover:border-slate-400"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            <Select value={rc.default} onValueChange={(v) => setRole(role, { default: v })}>
              <SelectTrigger className="h-9 bg-white" data-testid={`role-default-${role}`}><SelectValue /></SelectTrigger>
              <SelectContent>{cfg.models.filter((m) => rc.allowed.includes(m.id)).map((m) => <SelectItem key={m.id} value={m.id}>Par défaut : {m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <Button onClick={save} data-testid="role-models-save">Enregistrer</Button>
    </Card>
  );
}

const INBOX_STATUS = { importe: "Importé", sans_piece_jointe: "Sans pièce jointe utilisable", expediteur_inconnu: "Expéditeur inconnu (ignoré)", automatique: "Email automatique (ignoré)", authentification_echouee: "Expéditeur non authentifié (ignoré)" };

export function AdminInbox() {
  const [cfg, setCfg] = useState(null);
  const [kw, setKw] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/inbox").then((r) => { setCfg(r.data); setKw(r.data.inbox_keywords.join(", ")); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!cfg) return null;
  const save = async (enabled = cfg.inbox_enabled) => {
    try {
      await api.put("/admin/inbox", { inbox_enabled: enabled, inbox_keywords: kw.split(",").map((k) => k.trim()).filter(Boolean) });
      toast.success("Dépôt par email enregistré"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const check = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/inbox/check"); toast.success(`${data.imported} document(s) importé(s)`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card icon={Inbox} title="Dépôt de factures par email" testId="inbox-card">
      <p className="text-sm text-slate-600">
        Les collaborateurs inscrits transfèrent leurs factures à <span className="font-semibold" data-testid="inbox-address">{cfg.inbox_address}</span>.
        Toutes les 10 minutes, les pièces jointes (PDF, photos, Word, Excel) des emails dont l'objet contient un mot-clé sont déposées dans l'espace de l'expéditeur, lues par l'IA puis classées. La boîte est lue sans rien modifier ni supprimer ; les emails d'expéditeurs inconnus sont ignorés et signalés.
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <Switch checked={cfg.inbox_enabled} onCheckedChange={(v) => { setCfg({ ...cfg, inbox_enabled: v }); save(v); }} data-testid="inbox-switch" />
          {cfg.inbox_enabled ? "Activé" : "Désactivé"}
        </label>
        <Button variant="outline" onClick={check} disabled={busy || !cfg.inbox_enabled} data-testid="inbox-check-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Relever maintenant</Button>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mots-clés dans l'objet (séparés par des virgules)</label>
        <div className="flex gap-2 mt-1">
          <Input value={kw} onChange={(e) => setKw(e.target.value)} data-testid="inbox-keywords-input" />
          <Button variant="outline" onClick={() => save()} data-testid="inbox-keywords-save">Enregistrer</Button>
        </div>
      </div>
      {cfg.log.length > 0 && (
        <div className="border rounded-lg divide-y text-sm max-h-64 overflow-auto" data-testid="inbox-log">
          {cfg.log.map((l) => (
            <div key={l.id} className="px-3 py-2 flex flex-wrap justify-between gap-2">
              <span className="truncate max-w-[60%]"><span className="font-medium">{l.subject}</span> <span className="text-slate-500">· {l.from}</span></span>
              <span className="text-xs text-slate-500">{INBOX_STATUS[l.status] || l.status} · {fmtDate(l.at, true)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function AdminAccountant() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/accountant").then((r) => setEmail(r.data.accountant_email)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/admin/accountant", { accountant_email: email }); toast.success(email ? "Adresse du comptable enregistrée" : "Envoi mensuel désactivé"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sendNow = async () => {
    setBusy(true);
    try {
      await api.put("/admin/accountant", { accountant_email: email });
      const { data } = await api.post("/admin/accountant/send-now");
      if (data.sent) toast.success(`Export de ${data.period} envoyé (${data.count} facture(s))`);
      else toast.error("L'email n'a pas pu être envoyé : vérifiez l'adresse et la messagerie");
    }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card icon={Send} title="Export mensuel au comptable" testId="accountant-card">
      <p className="text-sm text-slate-600">Le 1er de chaque mois à 7h, le comptable reçoit par email l'export Excel des factures validées du mois précédent. Laissez vide pour ne rien envoyer.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="comptable@cabinet.fr" className="sm:w-80" data-testid="accountant-email-input" />
        <Button onClick={save} data-testid="accountant-save">Enregistrer</Button>
        <Button variant="outline" onClick={sendNow} disabled={busy} data-testid="accountant-send-now">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Envoyer le mois dernier maintenant</Button>
      </div>
    </Card>
  );
}
